import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plane, MapPin, Package, DollarSign, Settings, Link, Plus, Pencil, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function MastersPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("airports");
  const [airports, setAirports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [locServices, setLocServices] = useState([]);
  const [rates, setRates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [a, l, s, p, ls, r] = await Promise.all([
        fetch(`${API}/api/masters/airports`).then(r => r.json()),
        fetch(`${API}/api/masters/locations`).then(r => r.json()),
        fetch(`${API}/api/masters/services`).then(r => r.json()),
        fetch(`${API}/api/masters/packages`).then(r => r.json()),
        fetch(`${API}/api/masters/loc-services`).then(r => r.json()),
        fetch(`${API}/api/masters/rates`).then(r => r.json()),
      ]);
      setAirports(a); setLocations(l); setServices(s);
      setPackages(p); setLocServices(ls); setRates(r);
    } catch (e) { toast.error("Failed to load masters"); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = (tab) => {
    setEditItem(null);
    const defaults = {
      airports: { airport_code: "", city: "", airport_name: "", state: "", is_active: true },
      locations: { location_code: "", airport_code: "", terminal_number: "1", terminal_type: "Domestic", gate_type: "Departure", is_active: true },
      services: { service_code: "", service_name: "", airport_code: "", image_url: "", description: "", sla_hours: 3, is_active: true },
      packages: { package_code: "", package_name: "", service_codes_included: [], image_url: "", description: "", is_active: true },
      "loc-services": { loc_service_code: "", location_code: "", service_code: "", partner: "", contact_person: "", address: "", is_active: true },
      rates: { rate_code: "", loc_service_code: "", special_price: "", open_price: "", from_date: "", to_date: "", is_active: true },
    };
    setFormData(defaults[tab] || {});
    setDialogOpen(true);
  };

  const openEdit = (tab, item) => {
    setEditItem(item);
    setFormData({ ...item });
    setDialogOpen(true);
  };

  const getCodeField = (tab) => ({
    airports: "airport_code", locations: "location_code", services: "service_code",
    packages: "package_code", "loc-services": "loc_service_code", rates: "rate_code"
  }[tab]);

  const handleSave = async () => {
    const tab = activeTab;
    const codeField = getCodeField(tab);
    const code = editItem ? editItem[codeField] : formData[codeField];
    const method = editItem ? "PUT" : "POST";
    const url = editItem ? `${API}/api/masters/${tab}/${code}` : `${API}/api/masters/${tab}`;
    try {
      const body = { ...formData };
      if (tab === "rates") {
        body.open_price = parseFloat(body.open_price) || 0;
        body.special_price = body.special_price ? parseFloat(body.special_price) : null;
      }
      if (tab === "services") { body.sla_hours = parseFloat(body.sla_hours) || 3; }
      const res = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      toast.success(editItem ? "Updated" : "Created");
      setDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (tab, code) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      const res = await fetch(`${API}/api/masters/${tab}/${code}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete");
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const updateForm = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const renderActions = (tab, item, codeField) => (
    <div className="flex gap-1">
      <Button variant="ghost" size="sm" onClick={() => openEdit(tab, item)} data-testid={`edit-${item[codeField]}`}><Pencil className="h-3.5 w-3.5" /></Button>
      <Button variant="ghost" size="sm" onClick={() => handleDelete(tab, item[codeField])} data-testid={`delete-${item[codeField]}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="masters-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Master Data</h1>
          <p className="text-sm text-slate-500">Manage airports, locations, services, packages, and rates</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="airports" className="text-xs"><Plane className="h-3.5 w-3.5 mr-1" />Airports</TabsTrigger>
          <TabsTrigger value="locations" className="text-xs"><MapPin className="h-3.5 w-3.5 mr-1" />Locations</TabsTrigger>
          <TabsTrigger value="services" className="text-xs"><Settings className="h-3.5 w-3.5 mr-1" />Services</TabsTrigger>
          <TabsTrigger value="packages" className="text-xs"><Package className="h-3.5 w-3.5 mr-1" />Packages</TabsTrigger>
          <TabsTrigger value="loc-services" className="text-xs"><Link className="h-3.5 w-3.5 mr-1" />Loc-Service</TabsTrigger>
          <TabsTrigger value="rates" className="text-xs"><DollarSign className="h-3.5 w-3.5 mr-1" />Rates</TabsTrigger>
        </TabsList>

        {/* AIRPORTS */}
        <TabsContent value="airports">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Airports ({airports.length})</CardTitle>
              <Button size="sm" onClick={() => openCreate("airports")} data-testid="add-airport"><Plus className="h-4 w-4 mr-1" />Add Airport</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>City</TableHead><TableHead>Airport Name</TableHead><TableHead>State</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{airports.map(a => (
                  <TableRow key={a.airport_code}>
                    <TableCell className="font-mono font-bold">{a.airport_code}</TableCell>
                    <TableCell>{a.city}</TableCell>
                    <TableCell>{a.airport_name}</TableCell>
                    <TableCell>{a.state}</TableCell>
                    <TableCell><Badge variant={a.is_active ? "default" : "secondary"}>{a.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell>{renderActions("airports", a, "airport_code")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOCATIONS */}
        <TabsContent value="locations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Locations ({locations.length})</CardTitle>
              <Button size="sm" onClick={() => openCreate("locations")} data-testid="add-location"><Plus className="h-4 w-4 mr-1" />Add Location</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Airport</TableHead><TableHead>Terminal</TableHead><TableHead>Type</TableHead><TableHead>Gate</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{locations.map(l => (
                  <TableRow key={l.location_code}>
                    <TableCell className="font-mono font-bold">{l.location_code}</TableCell>
                    <TableCell>{l.airport_code}</TableCell>
                    <TableCell>T{l.terminal_number}</TableCell>
                    <TableCell><Badge variant="outline">{l.terminal_type}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{l.gate_type}</Badge></TableCell>
                    <TableCell>{renderActions("locations", l, "location_code")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SERVICES */}
        <TabsContent value="services">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Services ({services.length})</CardTitle>
              <Button size="sm" onClick={() => openCreate("services")} data-testid="add-service"><Plus className="h-4 w-4 mr-1" />Add Service</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Airport</TableHead><TableHead>SLA (hrs)</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{services.map(s => (
                  <TableRow key={s.service_code}>
                    <TableCell className="font-mono font-bold">{s.service_code}</TableCell>
                    <TableCell>{s.service_name}</TableCell>
                    <TableCell>{s.airport_code || "All"}</TableCell>
                    <TableCell>{s.sla_hours}h</TableCell>
                    <TableCell><Badge variant={s.is_active ? "default" : "secondary"}>{s.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell>{renderActions("services", s, "service_code")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PACKAGES */}
        <TabsContent value="packages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Packages ({packages.length})</CardTitle>
              <Button size="sm" onClick={() => openCreate("packages")} data-testid="add-package"><Plus className="h-4 w-4 mr-1" />Add Package</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Services</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{packages.map(p => (
                  <TableRow key={p.package_code}>
                    <TableCell className="font-mono font-bold">{p.package_code}</TableCell>
                    <TableCell>{p.package_name}</TableCell>
                    <TableCell className="text-xs">{(p.service_codes_included || []).join(", ")}</TableCell>
                    <TableCell><Badge variant={p.is_active ? "default" : "secondary"}>{p.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell>{renderActions("packages", p, "package_code")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOC-SERVICES */}
        <TabsContent value="loc-services">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Location-Services ({locServices.length})</CardTitle>
              <Button size="sm" onClick={() => openCreate("loc-services")} data-testid="add-loc-service"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Location</TableHead><TableHead>Service</TableHead><TableHead>Partner</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{locServices.map(ls => (
                  <TableRow key={ls.loc_service_code}>
                    <TableCell className="font-mono font-bold text-xs">{ls.loc_service_code}</TableCell>
                    <TableCell>{ls.location_code}</TableCell>
                    <TableCell>{ls.service_code}</TableCell>
                    <TableCell>{ls.partner}</TableCell>
                    <TableCell>{renderActions("loc-services", ls, "loc_service_code")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RATES */}
        <TabsContent value="rates">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Rates ({rates.length})</CardTitle>
              <Button size="sm" onClick={() => openCreate("rates")} data-testid="add-rate"><Plus className="h-4 w-4 mr-1" />Add Rate</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>LocService</TableHead><TableHead>Price</TableHead><TableHead>Special</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{rates.map(r => (
                  <TableRow key={r.rate_code}>
                    <TableCell className="font-mono font-bold">{r.rate_code}</TableCell>
                    <TableCell className="text-xs">{r.loc_service_code}</TableCell>
                    <TableCell className="font-semibold">Rs.{(r.open_price || 0).toLocaleString()}</TableCell>
                    <TableCell>{r.special_price ? `Rs.${r.special_price.toLocaleString()}` : "-"}</TableCell>
                    <TableCell className="text-xs">{r.from_date}</TableCell>
                    <TableCell className="text-xs">{r.to_date}</TableCell>
                    <TableCell>{renderActions("rates", r, "rate_code")}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit" : "Create"} {activeTab.replace("-", " ")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {activeTab === "airports" && <>
              <Input placeholder="Airport Code (e.g. DXN)" value={formData.airport_code || ""} onChange={e => updateForm("airport_code", e.target.value.toUpperCase())} disabled={!!editItem} />
              <Input placeholder="City" value={formData.city || ""} onChange={e => updateForm("city", e.target.value)} />
              <Input placeholder="Airport Name" value={formData.airport_name || ""} onChange={e => updateForm("airport_name", e.target.value)} />
              <Input placeholder="State" value={formData.state || ""} onChange={e => updateForm("state", e.target.value)} />
            </>}
            {activeTab === "locations" && <>
              <Input placeholder="Location Code" value={formData.location_code || ""} onChange={e => updateForm("location_code", e.target.value.toUpperCase())} disabled={!!editItem} />
              <Select value={formData.airport_code || ""} onValueChange={v => updateForm("airport_code", v)}>
                <SelectTrigger><SelectValue placeholder="Select Airport" /></SelectTrigger>
                <SelectContent>{airports.map(a => <SelectItem key={a.airport_code} value={a.airport_code}>{a.airport_code} - {a.city}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Terminal Number" value={formData.terminal_number || ""} onChange={e => updateForm("terminal_number", e.target.value)} />
              <Select value={formData.terminal_type || ""} onValueChange={v => updateForm("terminal_type", v)}>
                <SelectTrigger><SelectValue placeholder="Terminal Type" /></SelectTrigger>
                <SelectContent><SelectItem value="Domestic">Domestic</SelectItem><SelectItem value="International">International</SelectItem></SelectContent>
              </Select>
              <Select value={formData.gate_type || ""} onValueChange={v => updateForm("gate_type", v)}>
                <SelectTrigger><SelectValue placeholder="Gate Type" /></SelectTrigger>
                <SelectContent><SelectItem value="Departure">Departure</SelectItem><SelectItem value="Arrival">Arrival</SelectItem></SelectContent>
              </Select>
            </>}
            {activeTab === "services" && <>
              <Input placeholder="Service Code" value={formData.service_code || ""} onChange={e => updateForm("service_code", e.target.value.toUpperCase())} disabled={!!editItem} />
              <Input placeholder="Service Name" value={formData.service_name || ""} onChange={e => updateForm("service_name", e.target.value)} />
              <Select value={formData.airport_code || "all"} onValueChange={v => updateForm("airport_code", v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Airport (optional)" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Airports</SelectItem>{airports.map(a => <SelectItem key={a.airport_code} value={a.airport_code}>{a.airport_code} - {a.city}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Image URL" value={formData.image_url || ""} onChange={e => updateForm("image_url", e.target.value)} />
              <Input placeholder="Description" value={formData.description || ""} onChange={e => updateForm("description", e.target.value)} />
              <Input placeholder="SLA Hours" type="number" step="0.5" value={formData.sla_hours || 3} onChange={e => updateForm("sla_hours", e.target.value)} />
            </>}
            {activeTab === "packages" && <>
              <Input placeholder="Package Code" value={formData.package_code || ""} onChange={e => updateForm("package_code", e.target.value.toUpperCase())} disabled={!!editItem} />
              <Input placeholder="Package Name" value={formData.package_name || ""} onChange={e => updateForm("package_name", e.target.value)} />
              <Input placeholder="Service Codes (comma-separated)" value={(formData.service_codes_included || []).join(", ")} onChange={e => updateForm("service_codes_included", e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean))} />
              <Input placeholder="Image URL" value={formData.image_url || ""} onChange={e => updateForm("image_url", e.target.value)} />
              <Input placeholder="Description" value={formData.description || ""} onChange={e => updateForm("description", e.target.value)} />
            </>}
            {activeTab === "loc-services" && <>
              <Input placeholder="LocService Code" value={formData.loc_service_code || ""} onChange={e => updateForm("loc_service_code", e.target.value.toUpperCase())} disabled={!!editItem} />
              <Select value={formData.location_code || ""} onValueChange={v => updateForm("location_code", v)}>
                <SelectTrigger><SelectValue placeholder="Select Location" /></SelectTrigger>
                <SelectContent>{locations.map(l => <SelectItem key={l.location_code} value={l.location_code}>{l.location_code} ({l.airport_code} T{l.terminal_number} {l.terminal_type} {l.gate_type})</SelectItem>)}</SelectContent>
              </Select>
              <Select value={formData.service_code || ""} onValueChange={v => updateForm("service_code", v)}>
                <SelectTrigger><SelectValue placeholder="Select Service/Package" /></SelectTrigger>
                <SelectContent>
                  {services.map(s => <SelectItem key={s.service_code} value={s.service_code}>{s.service_code} - {s.service_name}</SelectItem>)}
                  {packages.map(p => <SelectItem key={p.package_code} value={p.package_code}>{p.package_code} - {p.package_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Partner" value={formData.partner || ""} onChange={e => updateForm("partner", e.target.value)} />
              <Input placeholder="Contact Person" value={formData.contact_person || ""} onChange={e => updateForm("contact_person", e.target.value)} />
              <Input placeholder="Address" value={formData.address || ""} onChange={e => updateForm("address", e.target.value)} />
            </>}
            {activeTab === "rates" && <>
              <Input placeholder="Rate Code" value={formData.rate_code || ""} onChange={e => updateForm("rate_code", e.target.value.toUpperCase())} disabled={!!editItem} />
              <Select value={formData.loc_service_code || ""} onValueChange={v => updateForm("loc_service_code", v)}>
                <SelectTrigger><SelectValue placeholder="Select LocService" /></SelectTrigger>
                <SelectContent>{locServices.map(ls => <SelectItem key={ls.loc_service_code} value={ls.loc_service_code}>{ls.loc_service_code} ({ls.location_code} / {ls.service_code})</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Open Price" type="number" value={formData.open_price || ""} onChange={e => updateForm("open_price", e.target.value)} />
              <Input placeholder="Special Price (optional)" type="number" value={formData.special_price || ""} onChange={e => updateForm("special_price", e.target.value)} />
              <Input placeholder="From Date (DD-MM-YYYY)" value={formData.from_date || ""} onChange={e => updateForm("from_date", e.target.value)} />
              <Input placeholder="To Date (DD-MM-YYYY)" value={formData.to_date || ""} onChange={e => updateForm("to_date", e.target.value)} />
            </>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-master">{editItem ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MastersPage;
